<template>
  <div>
    <account-home
      :tabs="tabs"
    >
      <template v-if="loading">
        <div id="loading-spinner-container">
          <v-progress-circular
            id="loading-spinner"
            indeterminate
            color="rgb(56, 125, 35)"
            :size="50"
          />
        </div>
      </template>
      <template v-else>
        <div
          v-if="showMobile"
          id="loan-status-timeline"
        >
          <template v-for="(payment, index) in transformedPayments">
            <loan-status-timeline-row
              :key="index"
              :payment="payment"
              @openPaymentPlan="openModal"
              @closePaymentPlan="closeModal"
            />
          </template>
          <loan-status-timeline-row :final-row="true" />
        </div>
        <template v-else>
          <table
            id="loan-status-table"
          >
            <col style="width:15%">
            <col style="width:25%">
            <col style="width:15%">
            <col style="width:15%">
            <col style="width:30%">
            <thead>
              <tr>
                <template v-for="(heading, index) in tableHeadings">
                  <th
                    :key="index"
                    v-html="heading"
                  />
                </template>
              </tr>
            </thead>
            <tbody>
              <loan-status-table-row
                v-for="(payment, index) in transformedPaymentsToDisplay"
                :key="index"
                :payment="payment"
                @openPaymentPlan="openModal"
              />
              <template v-if="showFinalRow">
                <loan-status-table-final-row />
              </template>
            </tbody>
            <tfoot v-if="showViewMoreButton">
              <tr>
                <td
                  id="view-more"
                  colspan="5"
                  @click="viewMoreAction"
                  v-html="viewMoreText"
                />
              </tr>
            </tfoot>
          </table>
        </template>
        <hr>
        <p
          v-if="!hasInstallmentsInDefault"
          id="bottom-message"
          v-html="bottomMessage"
        />
        <submit-button
          v-if="showMakeAPaymentPlanButton && !showMobile && hasInstallmentsInDefault"
          :text="bottomButtonText"
          :click-fn="makeAPaymentPlan"
        />
      </template>
    </account-home>
    <div
      v-if="showMakeAPaymentPlanButton && !loading && showMobile && hasInstallmentsInDefault"
      id="mobile-make-a-payment-container"
    >
      <submit-button
        :click-fn="makeAPaymentPlan"
        :text="bottomButtonText"
      />
    </div>
    <loan-status-modal
      v-if="showDialog"
      :show-dialog="showDialog"
      :payment-plan-id="selectedPaymentPlan"
      @closeModal="closeModal"
    />
  </div>
</template>

<script>
import { get } from 'vuex-pathify';
import AccountHome from '../components/AccountHome.vue';
import SubmitButton from '../components/SubmitButton.vue';
import LoanStatusTableRow from '../components/LoanStatusTableRow.vue';
import LoanStatusTableFinalRow from '../components/LoanStatusTableFinalRow.vue';
import LoanStatusTimelineRow from '../components/LoanStatusTimelineRow.vue';
import LoanStatusModal from '../components/LoanStatusModal.vue';
import captureRenderBlockingException from '../tracking/helpers/captureRenderBlockingException';
import tagRecordings from '../tracking/hotjar/tagRecordings';
import { optimizelyInstance, optimizelyUserId } from '../lib/optimizely';
import captureSentryException from '../tracking/sentry/captureSentryException';
import trackEvent from '../tracking/adobe/trackEvent';
import updateDataAnalyticsLayer from '../tracking/adobe/updateDataAnalyticsLayer';
import railsAxios from '../lib/railsAxios';
import emitUniversalMetricsEvent from '../lib/emitUniversalMetricsEvent';

export default {
  components: {
    AccountHome,
    LoanStatusTableRow,
    SubmitButton,
    LoanStatusTableFinalRow,
    LoanStatusTimelineRow,
    LoanStatusModal,
  },
  data() {
    return {
      windowWidth:   window.innerWidth,
      loading:       true,
      tableHeadings: [
        this.translate('installment'),
        this.translate('date'),
        this.translate('original_debt'),
        this.translate('outstanding_amount'),
        this.translate('status'),
      ],
      tableExpanded:              false,
      showDialog:                 false,
      selectedPaymentPlan:        null,
      selectedInstallmentNumber:  null,
      showMakeAPaymentPlanButton: false,
      inDefaultNoPmtPlan:         false,
    };
  },
  computed: {
    loanApplication: get('loanApplication'),
    tabs() {
      return [
        { name: this.translate('top_tab'), url: '/loan-status' },
      ];
    },
    transformedPayments() {
      const { payments } = this.$store.getters;
      const unpaidPayments = this.lodash.filter(payments, (p) => p.status === 'unpaid' || p.status === 'pending');
      const i = this.lodash.findIndex(payments, (p) => p === unpaidPayments[0]);
      if (i >= 0) {
        payments[i].status = 'next';
      }

      return payments;
    },
    transformedPaymentsToDisplay() {
      const allPayments = this.transformedPayments;
      const indexOfNextPayment = this.lodash.findIndex(allPayments, (p) => p.status === 'next');
      if (!this.tableExpanded && indexOfNextPayment >= 0) {
        return this.lodash.slice(allPayments, 0, indexOfNextPayment + 2);
      }
      return this.transformedPayments;
    },
    hasInstallmentsInDefault() {
      return this.$store.getters.hasInstallmentsInDefault;
    },
    allPaymentPlansOnTime() {
      return this.$store.getters.allPaymentPlansOnTime;
    },
    allDefaultedPayments() {
      return this.$store.getters.allDefaultedPayments;
    },
    maxDaysInDefault() {
      return this.$store.getters.maxDaysInDefault;
    },
    paymentInDefaultTooLong() {
      const maxDaysInDefaultForUnpaidInstallments = optimizelyInstance.getFeatureVariableInteger('show_loan_status_make_a_payment_button',
        'max_days_in_default_for_unpaid_installments', optimizelyUserId());
      const comparisonDate = new Date();
      comparisonDate.setDate(comparisonDate.getDate() - maxDaysInDefaultForUnpaidInstallments);

      const defaultedPaymentIndex = this.lodash.findIndex(this.allDefaultedPayments, (payment) => {
        const dueDate = new Date(payment.installment_due_date);
        return dueDate <= comparisonDate;
      });
      return defaultedPaymentIndex > -1;
    },
    topMessageFirstLine() {
      let message = null;
      if (this.hasInstallmentsInDefault && this.allPaymentPlansOnTime) {
        message = this.translate('top_message.first_line.loan_installment_in_default_payment_plan_on_time');
      } if (!this.allPaymentPlansOnTime) {
        message = this.translate('top_message.first_line.loan_installment_in_default_payment_plan_installment_in_default');
      } if (!this.hasInstallmentsInDefault && this.allPaymentPlansOnTime) {
        message = this.translate('top_message.first_line.loan_installments_on_time');
      }
      return message;
    },
    topMessageSecondLine() {
      let message = null;
      if (this.hasInstallmentsInDefault) {
        message = this.translate('top_message.second_line.loan_installment_in_default');
      } if (!this.hasInstallmentsInDefault && !this.allPaymentPlansOnTime) {
        message = this.translate('top_message.second_line.loan_installments_on_time_payment_plan_installment_in_default');
      } if (!this.hasInstallmentsInDefault && this.allPaymentPlansOnTime) {
        message = this.translate('top_message.second_line.loan_installments_not_in_default');
      }
      return message;
    },
    bottomMessage() {
      return this.translate('bottom_message');
    },
    bottomButtonText() {
      return this.translate('bottom_button_text');
    },
    flashMessageType() {
      let type = null;
      if (this.hasInstallmentsInDefault || !this.allPaymentPlansOnTime) {
        type = 'error';
      } else {
        type = 'success';
      }
      return type;
    },
    viewMoreText() {
      let text = null;
      if (this.tableExpanded) {
        text = `${this.translate('view_less')} &#9650;`;
      } else {
        text = `${this.translate('view_more')} &#9660;`;
      }
      return text;
    },
    showFinalRow() {
      return this.transformedPaymentsToDisplay.length >= this.$store.getters.payments.length;
    },
    showMobile() {
      return this.windowWidth < 700;
    },
    showViewMoreButton() {
      const allPayments = this.transformedPayments;
      const nextPaymentIndex = this.lodash.findIndex(allPayments, (payment) => payment.status === 'next');
      if (nextPaymentIndex < 0) {
        return false;
      }
      return nextPaymentIndex < allPayments.length - 2;
    },
  },
  async mounted() {
    await this.fetchData();
    this.setFlashMessage();
    this.addResizeListener();
    this.scrollToFirstDefaulted();
    this.setShowMakeAPaymentPlanButton();
    this.setDataLayerUpdates();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize());
  },
  methods: {
    translate(key) {
      return this.$t(`pages.loan_status.${key}`);
    },
    viewMoreAction() {
      this.tableExpanded = !this.tableExpanded;
    },
    onResize() {
      return () => { this.windowWidth = window.innerWidth; };
    },
    scrollToFirstDefaulted() {
      if (!this.showMobile) {
        return;
      }
      const elements = document.getElementsByClassName('defaulted');
      if (elements.length === 0) {
        return;
      }
      const requiredElement = elements[0];
      this.$nextTick(() => {
        const scrollParams = { block: 'center', behavior: 'smooth' };
        requiredElement.scrollIntoView(scrollParams);
      });
    },
    setFlashMessage() {
      this.flash().destroyAll();
      const flashMsg = `${this.topMessageFirstLine} ${this.topMessageSecondLine}`;
      this.flash(flashMsg, this.flashMessageType);
    },
    addResizeListener() {
      this.$nextTick(() => {
        window.addEventListener('resize', this.onResize());
      });
    },
    async fetchData() {
      try {
        await this.$store.dispatch('fetchLoanStatusData');
        this.loading = false;
      } catch (e) {
        captureRenderBlockingException(e);
        window.location.replace('/account_home?f=1');
      }
    },
    openModal(paymentPlanId, installmentNumber) {
      tagRecordings(['loan_status_modal_opened']);
      this.selectedPaymentPlan = paymentPlanId;
      this.selectedInstallmentNumber = installmentNumber;
      this.showDialog = true;
    },
    closeModal() {
      this.selectedPaymentPlan = null;
      this.selectedInstallmentNumber = null;
      this.showDialog = false;
    },
    setShowMakeAPaymentPlanButton() {
      try {
        if (optimizelyInstance.isFeatureEnabled('show_loan_status_make_a_payment_button', optimizelyUserId())) {
          if (this.paymentInDefaultTooLong) {
            this.showMakeAPaymentPlanButton = false;
          } else if (this.hasInstallmentsInDefault) {
            trackEvent('make_a_payment_plan_displayed');
            emitUniversalMetricsEvent('saw_loan_schedule');
            this.showMakeAPaymentPlanButton = true;
            this.inDefaultNoPmtPlan = true;
          }
        }
      } catch (e) {
        this.showMakeAPaymentPlanButton = false;
        captureSentryException(e);
      }
    },
    setDataLayerUpdates() {
      updateDataAnalyticsLayer({ maxDaysInDefault: this.maxDaysInDefault });
      const updates = {
        maxDaysInDefault:   this.maxDaysInDefault,
        inDefaultNoPmtPlan: this.inDefaultNoPmtPlan,
      };
      railsAxios.post('/api/update-analytics-data-layer', updates)
        .catch((e) => {
          captureRenderBlockingException(e);
        });
    },
    makeAPaymentPlan() {
      window.location = process.env.MAKE_A_PAYMENT_PLAN_REDIRECT_URL;
    },
  },
};
</script>

<style lang="scss" scoped>
  #top-message-first-line {
    margin-bottom: 0;
  }
  table {
    width: 100%;
    border: 1px solid rgb(238, 238, 238);
    color: black;
    font-size: 1.1em;
    th {
      background-color: rgb(238, 238, 238);
      font-weight: bold;
      text-align: center;
      padding: 1em 0.5em;
    }
  }
  #loan-status-timeline {
    margin-top: 2em;
  }
  hr {
    margin-top: 15px;
    margin-bottom: 15px;
  }
  #bottom-message {
    font-size: 0.75em;
    line-height: 1.25em;
  }
  #view-more {
    background-color: rgb(238, 238, 238);
    font-weight: bold;
    text-align: center;
    padding: 1em 0.5em;
    text-align: center;
    &:hover {
      cursor: pointer;
    }
  }
  #mobile-make-a-payment-container {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    border-top: 2px solid rgb(238, 238, 238);
    border-bottom: 1px solid rgb(56, 125, 35);
    background: white;
  }
  #submit-button {
    display: block;
    margin: 1em auto;
    box-shadow: 0px 4px 10px -2px rgba(0, 0, 0, 0.31);
    text-transform: none;
    color: black;
    font-size: 1.1em;
    width: 80%;
  }
  #loading-spinner-container {
    min-height: 200px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #loading-spinner {
    display: block;
  }
  @media (min-width: 700px) {
    #submit-button {
      width: auto;
    }
  }
</style>
